export class Result<T, E = Error> {
    public static Ok<T>(value: T): Result.Ok<T> {
        return new Result(value)
    }

    public static Err<E>(error: E): Result.Err<E> {
        return new Result(undefined, error)
    }

    private readonly ok: T | null | undefined = undefined
    private readonly err: E | null = null

    private constructor(ok: T)
    private constructor(ok: null | undefined, err: E)
    private constructor(ok: T | null | undefined, err: E | null = null) {
        if (err !== null) {
            this.err = err as E
        } else if (ok !== undefined) {
            this.ok = ok
        }
    }

    public unwrap(): T {
        if (this.isOk()) {
            return this.ok as T
        }

        if (this.isErr()) {
            throw this.err as E
        }

        throw new Error('Unknown error')
    }

    public unwrapOr(defaultValue: T): T {
        return this.ok ?? defaultValue
    }

    public unwrapOrNull(): T | null {
        return this.ok ?? null
    }

    public isOk(): this is Result.Ok<T> {
        return this.err === null && this.ok !== undefined
    }

    public isErr(): this is Result.Err<E> {
        return this.err !== null
    }

    public getErr(): this extends Result.Err<E> ? E : null {
        return this.err as this extends Result.Err<E> ? E : null
    }

    public toString = (): string => {
        if (this.isErr()) {
            const err = this.getErr()
            return `Result.Err: ${err instanceof Error ? err.message : err}`
        }

        return `Result.Ok: ${this.unwrapOrNull()}`
    }
}

export namespace Result {
    export type Err<E = Error> = Result<never, E>
    export type Ok<T = any> = Result<T, never>
}
